import { useCallback, useEffect, useRef, useState } from 'react'
import { isSuccessTerminated } from '~/constants/task-status'
import { AgentTaskStatus } from '~/protos/agents'

interface ProgressProps {
  isDone: boolean
  maxTimeCap: number
  taskStatus: AgentTaskStatus
}

export const useMaxCapProgress = ({ isDone, maxTimeCap, taskStatus }: ProgressProps) => {
  const [progress, setProgress] = useState(0)
  const startTimeRef = useRef<number | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const getRandomIncrement = useCallback(() => {
    return Math.random() * 0.5 + 0.1 // Random increment between 0.1 and 0.6
  }, [])

  const updateProgress = useCallback(() => {
    const now = Date.now()
    const elapsedTime = startTimeRef.current ? now - startTimeRef.current : 0
    const progressPercent = (elapsedTime / (maxTimeCap * 1000)) * 100

    setProgress(prevProgress => {
      if (prevProgress >= 98) return prevProgress
      const baseProgress = Math.min(progressPercent, 98)
      const randomProgress = Math.min(prevProgress + getRandomIncrement(), baseProgress)
      return Math.min(randomProgress, 98)
    })

    if (progressPercent < 98) {
      intervalRef.current = setTimeout(updateProgress, 100) // Update every 100ms
    } else {
      setProgress(98)
    }
  }, [getRandomIncrement, maxTimeCap])

  const reset = useCallback(() => {
    setProgress(0)
    startTimeRef.current = Date.now()
    if (intervalRef.current) {
      clearTimeout(intervalRef.current)
    }
    intervalRef.current = setTimeout(updateProgress, 100)
  }, [updateProgress])

  useEffect(() => {
    if (isDone) {
      setProgress(100)
      return
    }

    if (startTimeRef.current === null) {
      startTimeRef.current = Date.now()
    }

    intervalRef.current = setTimeout(updateProgress, 100)

    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current)
    }
  }, [isDone, updateProgress])

  useEffect(() => {
    if (isSuccessTerminated(taskStatus)) {
      setProgress(100)
    }
  }, [taskStatus])

  return { progress, reset }
}
